import { Project } from 'src/projects/types/Project';
import { RegionsEnum } from 'src/generated/operations.ts';

export const DEFAULT_PROJECT = 'default';

export class ProjectsRepository {
    private readonly store: LocalForage;

    private readonly region: RegionsEnum;

    public constructor(store: LocalForage, region: RegionsEnum) {
        this.store = store;
        this.region = region;
    }

    public getDefaultProjectName(): string {
        return `${DEFAULT_PROJECT}_${this.region}`;
    }

    public async init(): Promise<Project> {
        const defaultProject = await this.store.getItem<Project>(this.getDefaultProjectName());
        if (defaultProject === null || (defaultProject && defaultProject.createdAt === undefined)) {
            return this.create(this.getDefaultProjectName());
        }
        return defaultProject;
    }

    public async count(): Promise<number> {
        return this.store.length();
    }

    public async list(limit: number, offset: number): Promise<Project[]> {
        const keys = await this.store.keys();
        const results = await Promise.all(keys
            .slice(offset, offset + limit)
            .map((key) => this.store.getItem<Project>(key)));
        return results.filter((v) => v !== null) as Project[];
    }

    public async create(name: string): Promise<Project> {
        const check = await this.store.getItem(name);
        if (check !== null) {
            throw new Error(`project with name "${name}" already exist`);
        }
        const project: Project = {
            name,
            selections: [],
            createdAt: new Date(),
            updatedAt: new Date(),
        };
        await this.store.setItem(name, project);
        return project;
    }

    public async get(name: string): Promise<Project> {
        const project = await this.store.getItem<Project>(name);
        if (project === null) {
            throw new Error(`project with name "${name}" not found`);
        }
        return project;
    }

    public async update(project: Project): Promise<boolean> {
        await this.store.setItem(project.name, project);
        return true;
    }
}
